import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useWindowStore } from '@/shared/model';
import { SignOut } from '@phosphor-icons/react/dist/ssr/SignOut';
import type {
  AppView,
  TeamCapability,
  TeamRole,
  UserLevel,
  UserRole,
  ViewEligibility,
} from '@aegislab/shared';
import {
  getAppNavSections,
  isTeaserView,
  type AppNavSection,
  type AppNavSectionId,
} from '@/shared/config';
import { prefetchByView, type PrefetchViewer } from './lib/prefetchByView';
import { useGlitchLabel } from '@/shared/lib/useGlitchLabel';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { reducedMotion as reducedMotionQuery } from '@/shared/ui/theme';
import {
  relativePosition,
  sectionYPositions,
  estimateSectionHeight,
  fitScale,
  itemScale,
  itemOpacity,
  itemZIndex,
  isItemVisible,
} from './carouselMath';
import { useCarouselPhysics } from './useCarouselPhysics';
import { LiquidGlassFilter, useGlassHover } from '@/shared/ui/organisms';
import { getAppViewIcon } from '@/shared/ui/icons';
import * as s from './CarouselDock.css';
import { getDockSectionTone, getDockTone } from './carouselDockTones';

interface CarouselDockProps {
  onViewChange?: (view: AppView) => void;
  userLevel?: UserLevel;
  /** 역할 요건이 걸린 뷰(대표 콘솔)를 목록에서 가르는 축 — 미확정이면 PLAYER 로 본다 */
  roles?: UserRole[];
  /** 권한 요건이 걸린 뷰(근태 운영)는 팀 직책이 권한을 주므로 목록도 그 축을 본다 */
  teamRole?: TeamRole;
  teamCapabilities?: TeamCapability[];
  /** 서버가 판정한 창 자격(명단 내려받기). 확인 전·실패는 [] — 그 항목은 숨긴 채로 둔다 */
  viewEligibilities?: ViewEligibility[];
  /** 어느 팀(TF 포함)에서든 팀장인가 — 팀 운영 창 노출에 쓴다. */
  leadsTeam?: boolean;
  /** 캐퍼빌리티 조회가 아직 확정되지 않음 — 항목 부재를 "권한 없음"으로 단정하지 않는다 */
  capabilitiesPending?: boolean;
  /** 캐퍼빌리티 조회 실패 — caps 가 [] 로 붕괴한 상태. 숨김은 유지하되 이유를 말한다 */
  capabilitiesError?: boolean;
  onRetryCapabilities?: () => void;
  onLogout?: () => void;
  /**
   * 읽지 않은 알림이 있는 뷰. 독은 건수를 말하지 않고 유무만 말한다(2026-09-01 결정) —
   * 건수·종류·상호작용은 다이나믹 아일랜드 소관이다. 점등의 근거는 알림의 `isRead`
   * 하나이므로, 아일랜드가 읽음을 기록하면 이 집합이 저절로 줄어 소등이 따라온다.
   */
  unreadViews?: AppView[];
}

/** 기본값을 렌더마다 새 배열로 만들면 목록 계산이 매번 다시 돈다. */
const NO_ELIGIBILITIES: ViewEligibility[] = [];

const SECTION_GAP = 24; // 블록 edge-to-edge 목표 간격(px) — 실측 높이 기반 동적 배치
const MAGNIFICATION_RADIUS = 320;
const MIN_SCALE = 0.78;
const MAX_SCALE = 1.0;
const MIN_OPACITY = 0.45;
const MAX_OPACITY = 1.0;
const VISIBLE_RANGE = 2.5;

const SECTION_ACCENT_CLASS: Record<AppNavSectionId, string> = {
  self: s.sectionAccent_self,
  team: s.sectionAccent_team,
  organization: s.sectionAccent_organization,
  platform: s.sectionAccent_platform,
};

/**
 * 접근성 이름 — 눈에 보이는 상태는 소리로도 있다(DESIGN.md §상태 신호, 규칙 4).
 * 순서는 창 열림 · 새 알림 · 출시 예정이고 **마지막 자리는 고정**이다:
 * `carouselDockCapabilityAxis.test.tsx` 가 이름 끝을 정규식으로 잠가 뒀다.
 *
 * 현재 화면이면 열림 접미를 빼는 것은 아래 점 억제와 같은 이유다 — 보고 있는 창은
 * 당연히 열려 있어 같은 말을 두 번 하게 된다. 그래서 호출자가 `showOpen` 을 계산해 넘긴다.
 */
function dockButtonName(label: string, showOpen: boolean, hasUnread: boolean, teaser: boolean) {
  return (
    label +
    (showOpen ? ' (창 열림)' : '') +
    (hasUnread ? ' (새 알림)' : '') +
    (teaser ? ' (출시 예정)' : '')
  );
}

function SectionItemButton({
  view,
  label,
  isActive,
  isOpen,
  hasUnread,
  onClick,
  onHover,
  teaser = false,
}: {
  view: AppView;
  label: string;
  isActive: boolean;
  isOpen: boolean;
  hasUnread: boolean;
  onClick: () => void;
  onHover?: () => void;
  teaser?: boolean;
}) {
  const tone = getDockTone(view);
  const displayLabel = useGlitchLabel(label, teaser, 0.5);
  // 보고 있는 창에는 열림 점을 그리지 않는다 — 접근성 이름도 같은 조건을 쓴다.
  const showOpen = isOpen && !isActive;
  return (
    <button
      type="button"
      // 현재 화면은 면(글래스)이 숨쉬는 것으로만 말한다 — 라벨 색까지 뷰 톤으로 바꾸던
      // `dockButtonActive` 는 상태를 정체성 색으로 칠하는 자리라 걷었다.
      className={`${s.dockButton}${showOpen ? ` ${s.dockButtonOpen}` : ''}${hasUnread ? ` ${s.dockButtonNotify}` : ''}`}
      style={
        {
          '--dock-accent': tone.accent,
          '--dock-accent-soft': tone.accentSoft,
          '--dock-glow': tone.glow,
          '--dock-tint': tone.tint,
          '--dock-border': tone.border,
        } as React.CSSProperties
      }
      onPointerDown={(e) => e.stopPropagation()}
      onMouseEnter={onHover}
      onFocus={onHover}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      aria-label={dockButtonName(label, showOpen, hasUnread, teaser)}
    >
      <div
        className={`${s.itemGlassLayer}${isActive ? ` ${s.itemGlassLayerActive}` : ''}`}
        aria-hidden="true"
      />
      <div className={s.dockButtonContent}>
        <span className={s.dockIcon}>{getAppViewIcon(view)}</span>
        <span
          className={s.dockLabel}
          aria-hidden={teaser || undefined}
          style={teaser ? { fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' } : undefined}
        >
          {displayLabel}
        </span>
      </div>
    </button>
  );
}

function isKeyboardFocus(el: EventTarget): boolean {
  try {
    return (el as Element).matches(':focus-visible');
  } catch {
    return false;
  }
}

/** 브라우저의 포커스 자동 스크롤이 overflow:hidden 트랙을 밀어도 배치는 transform 으로만 하므로 되돌린다 */
function resetTrackScroll(e: React.UIEvent<HTMLElement>) {
  const el = e.currentTarget;
  if (el.scrollTop !== 0) el.scrollTop = 0;
  if (el.scrollLeft !== 0) el.scrollLeft = 0;
}

export function CarouselDock({
  onViewChange,
  userLevel = 'INTERN',
  roles = ['PLAYER'],
  teamRole,
  teamCapabilities = [],
  viewEligibilities = NO_ELIGIBILITIES,
  leadsTeam = false,
  capabilitiesPending = false,
  capabilitiesError = false,
  onRetryCapabilities,
  onLogout,
  unreadViews = [],
}: CarouselDockProps) {
  const prefersReducedMotion = useMediaQuery(reducedMotionQuery);
  // 확정 전에는 [] 를 근거로 "미출시"라고 단정하지 않는다 — 배지만 보류하고 항목은 그대로 둔다.
  // 창 본문 보호는 ViewGate 가 이미 한다.
  const capabilitiesSettled = !capabilitiesPending && !capabilitiesError;
  const { stateRef, handlers: glassHandlers } = useGlassHover();
  const sections = useMemo<AppNavSection[]>(
    () =>
      getAppNavSections({
        userLevel,
        teamCapabilities: teamCapabilities ?? [],
        roles,
        teamRole,
        viewEligibilities,
        leadsTeam,
      }),
    [userLevel, teamCapabilities, roles, teamRole, viewEligibilities, leadsTeam]
  );
  const prefetchViewer = useMemo<PrefetchViewer>(
    () => ({ level: userLevel, roles, teamRole, teamCapabilities, viewEligibilities }),
    [userLevel, roles, teamRole, teamCapabilities, viewEligibilities]
  );

  const { windowOpenWindows, windowFocused } = useWindowStore(
    useShallow((ws) => ({
      windowOpenWindows: ws.openWindows,
      windowFocused: ws.focusedWindow,
    }))
  );
  // 드래그 px→칸 환산값. 실측 섹션 높이가 들어온 뒤 아래 effect 가 채운다(0 이면 훅의 기본값)
  const dragStepRef = useRef(0);
  const getDragStepPx = useCallback(() => dragStepRef.current, []);
  const { offset, isDragging, trackRef, handlePointerDown, handleKeyDown, snapTo } =
    useCarouselPhysics({
      itemCount: sections.length,
      getDragStepPx,
      reducedMotion: prefersReducedMotion,
    });

  const handleSectionViewClick = useCallback(
    (sectionIndex: number, view: AppView) => {
      snapTo(sectionIndex);
      // Always call onViewChange — windowStore handles open/focus dedup
      onViewChange?.(view);
    },
    [onViewChange, snapTo]
  );

  // 섹션 블록의 실제 렌더 높이를 측정해 간격을 동적으로 계산한다.
  // offsetHeight는 transform: scale()의 영향을 받지 않으므로 자화 스케일과 무관하게 정확하다.
  // 섹션 id를 키로 저장하므로 구성(권한/역할)이 바뀌어도 리셋이 필요 없다(stale id는 단순 무시).
  const observerRef = useRef<ResizeObserver | null>(null);
  const elIdRef = useRef(new Map<HTMLElement, string>());
  const [heightById, setHeightById] = useState<Record<string, number>>({});

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      setHeightById((prev) => {
        let next = prev;
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          const id = elIdRef.current.get(el);
          if (!id) continue;
          const h = el.offsetHeight;
          if (h > 0 && prev[id] !== h) {
            if (next === prev) next = { ...prev };
            next[id] = h;
          }
        }
        return next;
      });
    });
    observerRef.current = ro;
    // 옵저버 생성(이 effect) 이전 첫 마운트에서 ref가 붙은 요소를 따라잡는다.
    for (const el of elIdRef.current.keys()) ro.observe(el);
    return () => {
      ro.disconnect();
      observerRef.current = null;
    };
  }, []);

  const handleSectionRef = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      if (!el) return undefined;
      elIdRef.current.set(el, id);
      observerRef.current?.observe(el);
      return () => {
        elIdRef.current.delete(el);
        observerRef.current?.unobserve(el);
      };
    },
    []
  );

  const heights = useMemo(
    () =>
      sections.map(
        (section) => heightById[section.id] ?? estimateSectionHeight(section.items.length)
      ),
    [sections, heightById]
  );
  // 트랙(독 본문) 높이 — 가장 큰 섹션이 들어가지 않으면 전체를 줄여 모든 버튼이 화면 안에 닿게 한다
  const [trackHeight, setTrackHeight] = useState(0);
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    setTrackHeight(el.clientHeight);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setTrackHeight(el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [trackRef]);
  const fit = useMemo(() => fitScale(trackHeight, Math.max(0, ...heights)), [trackHeight, heights]);
  const fittedHeights = useMemo(() => heights.map((h) => h * fit), [heights, fit]);

  // ponytail: 섹션 간격의 평균으로 환산한다 — 섹션마다 높이 차가 커지면 끄는 방향의 간격을 쓸 것
  useEffect(() => {
    dragStepRef.current = heights.length
      ? heights.reduce((sum, h) => sum + h, 0) / heights.length + SECTION_GAP
      : 0;
  }, [heights]);

  const positions = useMemo(() => {
    if (sections.length === 0) return [];
    const ys = sectionYPositions(offset, fittedHeights, SECTION_GAP);
    return sections.map((section, index) => {
      const relPos = relativePosition(index, offset, sections.length);
      const absRelPos = Math.abs(relPos);
      if (!isItemVisible(absRelPos, VISIBLE_RANGE)) return null;

      const y = ys[index] ?? 0;
      const distance = Math.abs(y);
      const scale = itemScale(distance, MAGNIFICATION_RADIUS, MIN_SCALE, MAX_SCALE) * fit;
      const opacity = itemOpacity(distance, MAGNIFICATION_RADIUS, MIN_OPACITY, MAX_OPACITY);
      const zIndex = itemZIndex(distance, MAGNIFICATION_RADIUS);

      return { section, index, y, scale, opacity, zIndex };
    });
  }, [sections, offset, fittedHeights, fit]);

  return (
    <nav
      className={s.carouselContainer}
      aria-label="Main navigation"
      onMouseMove={glassHandlers.onMouseMove}
      onMouseLeave={glassHandlers.onMouseLeave}
    >
      <LiquidGlassFilter animate={!prefersReducedMotion} hoverState={stateRef} />

      {capabilitiesError ? (
        <div role="alert" className={s.capabilityNotice}>
          <p className={s.capabilityNoticeText}>권한 정보를 불러오지 못했습니다</p>
          <button type="button" className={s.capabilityRetryButton} onClick={onRetryCapabilities}>
            다시 시도
          </button>
        </div>
      ) : capabilitiesPending ? (
        <p role="status" className={s.capabilityNotice}>
          권한 확인 중
        </p>
      ) : null}

      {/*
        키보드로 굴리는 스크롤 컨테이너라 `tabIndex` 를 갖되 `listbox` 는 아니다 — 자식이
        고를 `option` 이 아니라 섹션 그룹과 버튼이고, 고르는 것이 아니라 누르는 면이다.
        `listbox` 를 달아 두면 axe `aria-required-children` 이 critical 로 걸려 이 위젯의
        스토리가 신호를 그리기도 전에 떨어진다. `group` 은 필수 자식이 없고 이름을 갖는다.
      */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- 위·아래 화살표 키로 트랙을 굴리는 스크롤 컨테이너다. 위 설명대로 listbox 가 아니라 group 이라 린트가 비대화형으로 본다 */}
      <div
        ref={trackRef}
        id="dock-nav"
        role="group"
        aria-label="네비게이션"
        className={s.carouselTrack}
        data-dragging={isDragging}
        onPointerDown={handlePointerDown}
        onKeyDown={handleKeyDown}
        onScroll={resetTrackScroll}
        tabIndex={0}
      >
        {positions.map((pos) => {
          if (!pos) return null;
          const { section, index, y, scale, opacity, zIndex } = pos;
          const sectionTone = getDockSectionTone(section.id);
          return (
            <section
              key={section.id}
              ref={handleSectionRef(section.id)}
              role="group"
              aria-label={section.label}
              className={s.sectionBlock}
              onFocus={(e) => {
                // 마우스 누름의 포커스로 스냅하면 pointerup 전에 버튼이 움직여 클릭이 취소된다 — 키보드 포커스만 따른다
                if (isKeyboardFocus(e.target)) snapTo(index);
              }}
              style={
                {
                  '--dock-accent': sectionTone.accent,
                  '--dock-accent-soft': sectionTone.accentSoft,
                  '--dock-glow': sectionTone.glow,
                  '--dock-tint': sectionTone.tint,
                  '--dock-border': sectionTone.border,
                  transform: `translate(-50%, calc(-50% + ${y}px)) scale(${scale})`,
                  top: '50%',
                  opacity,
                  zIndex,
                } as React.CSSProperties
              }
            >
              <div className={s.sectionHeaderRow}>
                <span
                  className={`${s.sectionAccent} ${SECTION_ACCENT_CLASS[section.id]}`}
                  aria-hidden="true"
                />
                <h2 id={`dock-section-${section.id}`} className={s.sectionHeading}>
                  {section.label}
                </h2>
              </div>
              <div className={s.sectionItems}>
                {section.items.map((item) => {
                  const view: AppView = item.id;
                  const label: string = item.label;
                  return (
                    <SectionItemButton
                      key={item.id}
                      view={view}
                      label={label}
                      // 현재 화면은 스토어의 포커스 하나로만 정한다. MainPage 의
                      // `focusedWindow ?? 'quests'` 폴백을 함께 보면, 창이 하나도 없을 때
                      // 열려 있지도 않은 창이 현재 화면으로 표시된다.
                      isActive={windowFocused === view}
                      isOpen={windowOpenWindows.includes(view)}
                      hasUnread={unreadViews.includes(view)}
                      onClick={() => handleSectionViewClick(index, view)}
                      onHover={() => prefetchByView(view, prefetchViewer)}
                      teaser={capabilitiesSettled && isTeaserView(view, teamCapabilities)}
                    />
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <div className={s.logoutSection}>
        <button type="button" className={s.logoutButton} onClick={onLogout}>
          <div className={s.itemGlassLayer} aria-hidden="true" />
          <div className={s.itemContent}>
            <SignOut size={28} weight="duotone" />
            <span className={s.itemLabel}>로그아웃</span>
          </div>
        </button>
      </div>
    </nav>
  );
}
